import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { listApplications, getApplication, payApplication } from '../api';
import ApplicationView from '../components/ApplicationView';
import { FileText, Building2, Globe, Plus, Printer, Eye, Clock, CheckCircle2, AlertCircle, X, CreditCard } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const statusStyles = {
  in_review: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-400/30', icon: Clock },
  approved: { color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-400/30', icon: CheckCircle2 },
  documents_pending: { color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-400/30', icon: AlertCircle },
  rejected: { color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-400/30', icon: AlertCircle }
};

const MyApplications = () => {
  const { t, lang, dir } = useLang();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const printRef = useRef(null);

  const payNow = async (id) => {
    setPayingId(id);
    try {
      const r = await payApplication(id);
      if (r?.checkout_url) { window.location.href = r.checkout_url; return; }
      toast({ title: lang === 'ar' ? 'تعذّر بدء الدفع' : 'Could not start payment', variant: 'destructive' });
    } catch (e) {
      toast({ title: e?.response?.data?.detail || (lang === 'ar' ? 'تعذّر بدء الدفع' : 'Could not start payment'), variant: 'destructive' });
    } finally { setPayingId(null); }
  };

  useEffect(() => {
    listApplications().then((d) => setApps(d || [])).catch(() => setApps([])).finally(() => setLoading(false));
  }, []);

  const openView = async (id) => {
    try {
      const full = await getApplication(id);
      setViewing(full);
    } catch {
      toast({ title: lang === 'ar' ? 'تعذّر التحميل' : 'Failed to load', variant: 'destructive' });
    }
  };

  const doPrint = () => {
    if (!viewing) return;
    const el = printRef.current;
    if (!el) return window.print();
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) { window.print(); return; }
    const head = Array.from(document.head.querySelectorAll('link[rel=stylesheet], style'))
      .map((n) => n.outerHTML).join('\n');
    const html = `<!DOCTYPE html><html lang="${lang}" dir="${lang === 'ar' ? 'rtl' : 'ltr'}"><head><meta charset="utf-8"><title>${viewing.id}</title>${head}<style>
      body{margin:0;padding:24px;background:#fff;color:#f8fafc;}
      .application-printable{box-shadow:none !important;max-width:100% !important;margin:0 !important;}
      @page { size: A4; margin: 12mm; }
      @media print { body { padding:0; } }
    </style></head><body>${el.outerHTML}<script>window.onload=()=>{setTimeout(()=>{window.print();},400);};window.onafterprint=()=>{window.close();};</script></body></html>`;
    w.document.open(); w.document.write(html); w.document.close();
  };

  return (
    <div className="pt-28 pb-16 min-h-screen" dir={dir}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#2563eb] mb-3">— {t('nav.apps')} —</div>
            <h1 className="font-display text-5xl text-[#0a0a0a]">{t('apps.title')}</h1>
            <p className="mt-3 text-[#111827]/60">{t('apps.subtitle')}</p>
          </div>
          <Link to="/SetupWizard">
            <Button data-testid="new-application-btn" className="gold-button rounded-md h-11 px-6 text-sm"><Plus className="w-4 h-4 me-2" /> {t('apps.new_app')}</Button>
          </Link>
        </div>

        {loading ? (
          <div className="glass-card rounded-2xl p-16 text-center text-[#111827]/60">…</div>
        ) : apps.length === 0 ? (
          <div className="glass-card rounded-2xl p-16 text-center">
            <FileText className="w-12 h-12 text-[#2563eb]/40 mx-auto mb-4" />
            <p className="text-[#111827]/60 mb-6">{t('apps.no_apps')}</p>
            <Link to="/SetupWizard"><Button className="gold-button rounded-md h-11 px-6">{t('apps.new_app')}</Button></Link>
          </div>
        ) : (
          <div className="space-y-4">
            {apps.map((a) => {
              const s = statusStyles[a.status] || statusStyles.in_review;
              const SIcon = s.icon;
              const TypeIcon = a.type === 'mainland' ? Building2 : Globe;
              const displayName = a.company_name || a.companyName;
              const displayZone = a.zone_name || a.zone || '—';
              const displayDate = (a.created_at || a.submitted || '').toString().split('T')[0];
              return (
                <div key={a.id} data-testid={`app-row-${a.id}`} className="glass-card rounded-2xl p-6 md:p-8 grid md:grid-cols-12 gap-6 items-center">
                  <div className="md:col-span-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-[#2563eb]/10 border border-[#2563eb]/25 flex items-center justify-center">
                        <TypeIcon className="w-5 h-5 text-[#3b82f6]" />
                      </div>
                      <div>
                        <div className="font-display text-xl text-[#0a0a0a]">{displayName}</div>
                        <div className="text-xs text-[#111827]/50 mt-0.5">{displayZone}</div>
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#111827]/40">{t('apps.ref')}</div>
                    <div className="text-sm text-[#3b82f6] font-mono mt-1">{a.id}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#111827]/40">{t('apps.submitted')}</div>
                    <div className="text-sm text-[#111827]/80 mt-1">{displayDate}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${s.bg} ${s.border} border`}>
                      <SIcon className={`w-3 h-3 ${s.color}`} />
                      <span className={`text-xs ${s.color}`}>{t('apps.status.' + a.status)}</span>
                    </div>
                    <div className="mt-2">
                      <Progress value={a.progress || 15} className="h-1 bg-[#2563eb]/10" />
                      <div className="text-[10px] text-[#111827]/40 mt-1">{a.progress || 15}%</div>
                    </div>
                  </div>
                  <div className="md:col-span-2 flex flex-col items-start md:items-end gap-2" data-testid={`app-payment-${a.id}`}>
                    {a.estimate?.total > 0 && (
                      <div className="text-end">
                        <div className="text-[10px] uppercase tracking-widest text-[#111827]/40">{t('wizard.total_due')}</div>
                        <div className="text-sm text-[#0a0a0a] font-semibold tabular-nums">{Number(a.estimate.total).toLocaleString('en-US')} AED</div>
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap md:justify-end">
                      {a.payment_status === 'paid' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-400/30 text-xs text-blue-600" data-testid={`paid-badge-${a.id}`}><CheckCircle2 className="w-3 h-3" /> {t('wizard.paid')}</span>
                      ) : a.estimate?.total > 0 ? (
                        <Button data-testid={`pay-app-${a.id}`} onClick={() => payNow(a.id)} disabled={payingId === a.id} className="gold-button rounded-md h-9 px-3 text-xs"><CreditCard className="w-3.5 h-3.5 me-1" /> {payingId === a.id ? '...' : t('wizard.pay_now')}</Button>
                      ) : null}
                      <Button data-testid={`view-app-${a.id}`} onClick={() => openView(a.id)} className="ghost-button rounded-md h-9 px-3 text-xs bg-transparent"><Eye className="w-3.5 h-3.5 me-1" /> {t('apps.view')}</Button>
                      <Button data-testid={`print-app-${a.id}`} onClick={() => openView(a.id)} className="ghost-button rounded-md h-9 px-3 text-xs bg-transparent" title={lang === 'ar' ? 'طباعة / PDF' : 'Print / PDF'}><Printer className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* View + Print Dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-white border-none max-w-4xl max-h-[95vh] overflow-y-auto p-0">
          <div className="p-4 flex justify-end gap-2 border-b border-gray-200 no-print bg-gray-50">
            <Button data-testid="app-print-btn" onClick={doPrint} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-md h-9 px-4 text-xs">
              <Printer className="w-3.5 h-3.5 me-2" /> {lang === 'ar' ? 'طباعة / حفظ PDF' : 'Print / Save PDF'}
            </Button>
            <Button data-testid="app-close-btn" onClick={() => setViewing(null)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md h-9 px-4 text-xs"><X className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'إغلاق' : 'Close'}</Button>
          </div>
          <ApplicationView ref={printRef} application={viewing} lang={lang} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyApplications;
